/**
 * JoinWorkspacePage.jsx
 *
 * /join/:token, in the sign-in pages' shell (AuthShell + LoginPage.css):
 * the first pidly screen an invited person sees. It looks the token up,
 * says what they are joining and as what, and joins. Signed out, the
 * link carries through to sign-up as the invitation itself.
 *
 * A "project" here is what the UI calls an organisation; a "workspace"
 * is one of its drawing sets. The four token kinds the server issues
 * (project or workspace join link, project or workspace invite link) are
 * tried in turn.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiFetch from '../../utils/apiFetch';
import { clearAuth } from '../../utils/authStorage';
import { setUserScopedString } from '../../utils/userScopedStorage';
import { AuthPage } from '../LoginPage/AuthShell';
import '../LoginPage/LoginPage.css';

const ROLE_WORD = { owner: 'an admin', member: 'a member', guest: 'a guest', editor: 'an editor', viewer: 'a viewer' };

export default function JoinWorkspacePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);   // { kind, name, memberCount, role?, label?, expiresAt?, description?, client? }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  const isAuthenticated =
    localStorage.getItem('authenticated') === 'true' ||
    sessionStorage.getItem('authenticated') === 'true' ||
    !!localStorage.getItem('authToken') ||
    !!sessionStorage.getItem('authToken');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const gone = async (res) => { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'This invite link is no longer valid'); };
        const inviteRes = await apiFetch(`/api/project-invite/${encodeURIComponent(token)}`);
        if (inviteRes.ok) {
          const d = await inviteRes.json();
          if (!cancelled && d.success) { setTarget({ kind: 'invite', name: d.project.name, memberCount: d.project.memberCount, description: d.project.description, client: d.project.client, role: d.invite.role, label: d.invite.label, expiresAt: d.invite.expiresAt }); return; }
        } else if (inviteRes.status === 410) await gone(inviteRes);
        const orgInvRes = await apiFetch(`/api/org-invite/${encodeURIComponent(token)}`);
        if (orgInvRes.ok) {
          const d = await orgInvRes.json();
          if (!cancelled && d.success) { setTarget({ kind: 'org-invite', name: d.org.name, memberCount: d.org.memberCount, role: d.invite.role, label: d.invite.label, expiresAt: d.invite.expiresAt }); return; }
        } else if (orgInvRes.status === 410) await gone(orgInvRes);
        const r = await apiFetch(`/api/join/${encodeURIComponent(token)}`);
        const d = await r.json();
        if (!cancelled && d.success) { setTarget({ kind: 'org', name: d.org.name, memberCount: d.org.memberCount }); return; }
        const r2 = await apiFetch(`/api/project-join/${encodeURIComponent(token)}`);
        const d2 = await r2.json();
        if (!cancelled) {
          if (!d2.success) throw new Error(d2.error || 'This link is not a pidly invitation');
          setTarget({ kind: 'project', name: d2.project.name, memberCount: d2.project.memberCount, description: d2.project.description, client: d2.project.client });
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const toSignUp = () => {
    // Sign up, verify the email, and land back here to join. (A new
    // account waits for pidly's approval before the join goes through.)
    try { localStorage.setItem('pidly.postLoginRedirect', `/join/${token}`); } catch { /* private mode */ }
    navigate(`/login?join=${encodeURIComponent(token)}`);
  };

  async function handleJoin() {
    if (!isAuthenticated) { toSignUp(); return; }
    setJoining(true); setJoinError('');
    try {
      const endpoint = target.kind === 'invite' ? `/api/project-invite/${encodeURIComponent(token)}`
        : target.kind === 'org-invite' ? `/api/org-invite/${encodeURIComponent(token)}`
        : target.kind === 'project' ? `/api/project-join/${encodeURIComponent(token)}`
        : `/api/join/${encodeURIComponent(token)}`;
      const r = await apiFetch(endpoint, { method: 'POST' });
      if (r.status === 401) {
        // A stale session: clear it and come back through sign-in.
        try { clearAuth(); } catch { /* nothing to clear */ }
        toSignUp();
        return;
      }
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Could not join');
      if (target.kind === 'project' || target.kind === 'invite') {
        navigate(`/project/${d.projectId}`);
      } else {
        setUserScopedString('pidly.activeScope', d.orgId);
        navigate('/projects');
      }
    } catch (e) {
      setJoinError(e.message);
      setJoining(false);
    }
  }

  // Signed in already: join as soon as the link resolves (the sign-in
  // bounce lands here, and a second click would be a tax).
  const autoJoinedRef = useRef(false);
  useEffect(() => {
    if (autoJoinedRef.current || loading || error || !target || !isAuthenticated) return;
    autoJoinedRef.current = true;
    handleJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, target, isAuthenticated]);

  const isWorkspace = target && (target.kind === 'project' || target.kind === 'invite');
  const roleWord = target?.role ? (ROLE_WORD[target.role] || target.role) : null;

  return (
    <AuthPage>
      <div className="login-verify">
        {loading ? (
          <p className="login-note">Checking the link…</p>
        ) : error ? (
          <>
            <h2>This link does not work</h2>
            <p>{error}</p>
            <p className="login-note">Ask the person who sent it for a new one.</p>
            <button className="login-submit-btn" onClick={() => navigate('/')}>Go to pidly</button>
          </>
        ) : (
          <>
            <p className="login-note">You are invited to a {isWorkspace ? 'workspace' : 'project'}</p>
            <h2>{target.name}</h2>
            <p>
              {target.memberCount} member{target.memberCount === 1 ? '' : 's'}
              {isWorkspace && target.client ? `, ${target.client}` : ''}
              {roleWord ? `. You join as ${roleWord}` : ''}
              {target.label ? ` (${target.label})` : ''}
              {target.expiresAt ? `. The link expires ${new Date(target.expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}` : ''}
              .
            </p>
            {joinError && <p className="login-error">{joinError}</p>}
            <button className="login-submit-btn" onClick={handleJoin} disabled={joining}>
              {joining ? 'Joining…' : isAuthenticated ? `Join ${target.name}` : 'Create an account to join'}
            </button>
            {!isAuthenticated && (
              <p className="login-note">Already have a pidly account? <button type="button" className="login-link-btn" onClick={() => { try { localStorage.setItem('pidly.postLoginRedirect', `/join/${token}`); } catch { /* private mode */ } navigate('/login'); }}>Sign in</button></p>
            )}
          </>
        )}
      </div>
    </AuthPage>
  );
}
